import React, { useState, useEffect } from 'react';
import { legalConsentAPI as legalAPI } from '../utils/api';
import { Shield, CheckCircle } from 'lucide-react';

const ACCENT = '#42a4bf';

// `brand` (optional): white-label partner branding { platform_name, logo_url }.
// When set, the modal renders ZERO Euphoric marks — the tenant's contract is
// with the partner, and this chrome must carry the partner's identity only.
export default function LegalReacceptanceModal({ documents, onAccepted, brand = null }) {
  const [accepted,    setAccepted]    = useState({});
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [logoFailed,  setLogoFailed]  = useState(false);

  // Keep session alive while modal is open — ping every 90 seconds.
  // Without this, reading the 3 documents in new tabs causes 30-min
  // inactivity timer to fire and log the user out mid-acceptance.
  useEffect(() => {
    const ping = () => {
      // Simulate activity to reset the inactivity timer
      window.dispatchEvent(new MouseEvent('mousedown'));
    };
    ping(); // immediate ping on mount
    const interval = setInterval(ping, 90_000);
    return () => clearInterval(interval);
  }, []);

  const allAccepted = documents.every(d => accepted[d.key]);
  const toggle = (key) => setAccepted(prev => ({ ...prev, [key]: !prev[key] }));

  const handleAccept = async () => {
    if (!allAccepted) return;
    setLoading(true);
    setError('');
    try {
      await legalAPI.reaccept(documents.map(d => d.key));
      onAccepted();
    } catch {
      setError('Failed to record your acceptance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const DARK    = '#0f172a';
  const BODY    = '#475569';
  const MUTED   = '#94a3b8';
  const BORDER  = '#f1f5f9';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
      background: 'rgba(15,23,42,0.75)', backdropFilter: 'blur(8px)',
      fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif",
    }}>
      <div style={{
        width: '100%', maxWidth: 520,
        background: '#ffffff',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)',
      }}>

        {/* 4px gradient accent bar — exact match to email template */}
        <div style={{
          height: 4,
          background: 'linear-gradient(90deg,#42a4bf 0%,#6498a0 35%,#a98064 70%,#868c82 100%)',
        }} />

        {/* Logo bar */}
        <div style={{
          padding: '24px 36px 20px',
          background: '#ffffff',
          borderBottom: `1px solid ${BORDER}`,
        }}>
          {brand ? (
            brand.logo_url && !logoFailed ? (
              <img
                src={brand.logo_url}
                alt={brand.platform_name || ''}
                style={{ height: 32, width: 'auto', maxWidth: 200, display: 'block' }}
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span style={{ fontWeight: 700, fontSize: 16, color: DARK, letterSpacing: 1 }}>
                {(brand.platform_name || 'Terms of Use').toUpperCase()}
              </span>
            )
          ) : logoFailed ? (
            <span style={{ fontWeight: 700, fontSize: 16, color: ACCENT, letterSpacing: 1 }}>
              EUPHORIC AI
            </span>
          ) : (
            <img
              src="https://dashboard.euphoricai.io/euphoric-logo.png"
              alt="Euphoric AI"
              style={{ height: 32, width: 'auto', display: 'block' }}
              onError={() => setLogoFailed(true)}
            />
          )}
        </div>

        {/* Main content */}
        <div style={{ padding: '28px 36px 0' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: DARK }}>
            {brand ? 'Before you continue' : 'Legal Documents Updated'}
          </h2>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: BODY, lineHeight: 1.6 }}>
            {brand
              ? `Please review and accept the platform terms to continue${brand.platform_name ? ` using ${brand.platform_name}` : ''}.`
              : "We've updated our legal documents. Please review and accept each one to continue using Euphoric OS."}
          </p>

          {/* Document list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 280, overflowY: 'auto' }}>
            {documents.map((doc) => (
              <div key={doc.key}
                onClick={() => toggle(doc.key)}
                style={{
                  border: `1.5px solid ${accepted[doc.key] ? ACCENT : '#e2e8f0'}`,
                  borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
                  background: accepted[doc.key] ? '#f0f9ff' : '#ffffff',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                }}
              >
                {/* Checkbox */}
                <div style={{
                  width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 2,
                  border: `2px solid ${accepted[doc.key] ? ACCENT : '#cbd5e1'}`,
                  background: accepted[doc.key] ? ACCENT : '#ffffff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}>
                  {accepted[doc.key] && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <a href={doc.url} target="_blank" rel="noopener noreferrer"
                       onClick={e => e.stopPropagation()}
                       style={{ fontWeight: 600, fontSize: 14, color: DARK, textDecoration: 'none',
                                borderBottom: `1px solid ${DARK}20` }}>
                      {doc.title} ↗
                    </a>
                    {doc.is_new ? (
                      <span style={{
                        fontSize: 11, background: ACCENT, color: '#fff',
                        padding: '2px 9px', borderRadius: 999, fontWeight: 600, flexShrink: 0,
                      }}>New</span>
                    ) : (
                      <span style={{
                        fontSize: 11, color: ACCENT, border: `1px solid ${ACCENT}`,
                        padding: '2px 9px', borderRadius: 999, fontWeight: 600, flexShrink: 0,
                      }}>v{doc.accepted_version} → v{doc.current_version}</span>
                    )}
                  </div>
                  <p style={{ margin: '5px 0 3px', fontSize: 12, color: BODY, lineHeight: 1.5 }}>{doc.summary}</p>
                  <p style={{ margin: 0, fontSize: 11, color: MUTED }}>Effective {doc.effective}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '20px 36px 28px' }}>
          {/* Gradient divider — matches email template */}
          <div style={{
            height: 1, margin: '0 0 20px',
            background: 'linear-gradient(90deg,transparent 0%,#e2e8f0 20%,#e2e8f0 80%,transparent 100%)',
          }} />

          {error && (
            <p style={{ color: '#ef4444', fontSize: 13, textAlign: 'center', marginBottom: 12 }}>{error}</p>
          )}

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 18 }}>
            <Shield size={13} color={MUTED} style={{ marginTop: 2, flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
              By clicking accept, your acceptance is recorded with your IP address, device, and
              timestamp — creating an immutable legal record.
            </p>
          </div>

          <button
            onClick={handleAccept}
            disabled={!allAccepted || loading}
            style={{
              width: '100%', padding: '13px 0', borderRadius: 10, border: 'none',
              fontWeight: 600, fontSize: 14,
              cursor: allAccepted && !loading ? 'pointer' : 'not-allowed',
              background: allAccepted && !loading
                ? `linear-gradient(90deg, ${ACCENT} 0%, #6498a0 100%)`
                : '#e2e8f0',
              color: allAccepted && !loading ? '#ffffff' : MUTED,
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {loading ? (
              <>
                <div style={{
                  width: 16, height: 16,
                  border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                  borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                }} />
                Recording acceptance…
              </>
            ) : allAccepted ? (
              <><CheckCircle size={16} /> Accept & Continue</>
            ) : (
              `Please accept all ${documents.length} document${documents.length > 1 ? 's' : ''} above`
            )}
          </button>

          {/* Footer links — Euphoric chrome only when NOT white-label */}
          {brand ? (
            brand.platform_name && (
              <p style={{ margin: '20px 0 0', textAlign: 'center', fontSize: 11, color: '#cbd5e1' }}>
                © {new Date().getFullYear()} {brand.platform_name}. All rights reserved.
              </p>
            )
          ) : (
            <>
              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <span style={{ fontSize: 12, color: MUTED }}>Questions? </span>
                <a href="mailto:legal@euphoricai.io"
                   style={{ fontSize: 12, color: ACCENT, textDecoration: 'none', fontWeight: 500 }}>
                  Contact our legal team
                </a>
                <span style={{ color: '#cbd5e1', margin: '0 8px' }}>|</span>
                <a href="https://euphoricai.io" target="_blank" rel="noopener noreferrer"
                   style={{ fontSize: 12, color: ACCENT, textDecoration: 'none' }}>
                  euphoricai.io
                </a>
              </div>
              <p style={{ margin: '10px 0 0', textAlign: 'center', fontSize: 11, color: '#cbd5e1' }}>
                © {new Date().getFullYear()} Euphoric AI. All rights reserved.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
